import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Wand2, Plus, Trash2, Pencil, Eye, EyeOff, ChevronUp, ChevronDown, Loader2, X, Check, Upload, Image as ImageIcon, Star } from 'lucide-react';
import { api, publicFileUrl } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import ImagePicker from '../components/ImagePicker';

const EMPTY = {
    type: 'card', title: '', body: '', image: '', link_url: '', link_label: '',
    placement: 'dashboard', bg_color: '#2C4C3B', text_color: '#FFFFFF', active: true, order: 0,
    featured: false, start_at: '', end_at: '',
};

const todayStr = () => new Date().toISOString().slice(0, 10);
const scheduleState = (it) => {
    const t = todayStr();
    if (it.start_at && t < it.start_at) return { label: 'Scheduled', cls: 'bg-sky-100 text-sky-700' };
    if (it.end_at && t > it.end_at) return { label: 'Expired', cls: 'bg-gray-200 text-gray-500' };
    return null;
};

const imgSrc = (src) => (!src ? '' : (/^https?:\/\//i.test(src) ? src : publicFileUrl(src)));

function Preview({ item }) {
    const src = imgSrc(item.image);
    if (item.type === 'banner') {
        return (
            <div className="rounded-2xl px-4 py-3 flex items-center gap-3" style={{ backgroundColor: item.bg_color, color: item.text_color }}>
                {src && <img src={src} alt="" className="w-10 h-10 rounded-xl object-cover" />}
                <div className="min-w-0 flex-1">
                    {item.title && <div className="font-semibold text-sm">{item.title}</div>}
                    {item.body && <div className="text-xs opacity-90">{item.body}</div>}
                </div>
                {item.link_url && <span className="text-xs font-bold underline">{item.link_label || 'Open'}</span>}
            </div>
        );
    }
    return (
        <div className="rounded-3xl border border-brand-border overflow-hidden bg-brand-card">
            {src && <img src={src} alt="" className="w-full h-32 object-cover" />}
            <div className="p-4">
                {item.title && <h3 className="font-serif text-lg text-brand-ink">{item.title}</h3>}
                {item.body && <p className="text-sm text-brand-ink2 mt-1 whitespace-pre-line">{item.body}</p>}
                {item.link_url && <span className="mt-2 inline-block text-sm font-medium text-brand-green">{item.link_label || 'Learn more'} →</span>}
            </div>
        </div>
    );
}

function ContentForm({ initial, onCancel, onSaved }) {
    const [form, setForm] = useState(initial);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const uploadImg = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const r = await api.post('/upload?public=true', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            set('image', r.data.path);
            toast.success('Image uploaded');
        } catch { toast.error('Upload failed'); }
        finally { setUploading(false); }
    };

    const save = async () => {
        if (!form.title?.trim() && !form.body?.trim()) { toast.error('Add a title or body'); return; }
        let lu = form.link_url?.trim();
        if (lu && !/^https?:\/\//i.test(lu) && !lu.startsWith('/')) lu = `https://${lu}`;
        setSaving(true);
        try {
            const payload = { ...form, link_url: lu };
            if (form.id) await api.put(`/admin/app-content/${form.id}`, payload);
            else await api.post('/admin/app-content', payload);
            toast.success('Saved');
            onSaved();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
        finally { setSaving(false); }
    };

    return (
        <div className="bg-brand-bg2 border border-brand-border rounded-2xl p-4 space-y-3" data-testid="content-form">
            <div className="flex items-center justify-between">
                <h3 className="font-serif text-base text-brand-ink">{form.id ? 'Edit block' : 'New block'}</h3>
                <button onClick={onCancel} data-testid="content-form-cancel" className="text-brand-ink3 hover:text-brand-ink"><X size={16} /></button>
            </div>

            <Preview item={form} />

            <div className="grid grid-cols-2 gap-2">
                <label className="block">
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">Type</span>
                    <select data-testid="content-type" value={form.type} onChange={(e) => set('type', e.target.value)} className="mt-1 w-full bg-brand-card border border-brand-border rounded-xl px-3 py-2 text-sm text-brand-ink">
                        <option value="card">Card</option>
                        <option value="banner">Banner</option>
                    </select>
                </label>
                <label className="block">
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">Where to show</span>
                    <select data-testid="content-placement" value={form.placement} onChange={(e) => set('placement', e.target.value)} className="mt-1 w-full bg-brand-card border border-brand-border rounded-xl px-3 py-2 text-sm text-brand-ink">
                        <option value="dashboard">Dashboard</option>
                        <option value="login">Login screen</option>
                        <option value="global">Everywhere</option>
                    </select>
                </label>
            </div>

            <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">Title</span>
                <input data-testid="content-title" value={form.title} onChange={(e) => set('title', e.target.value)} className="mt-1 w-full bg-brand-card border border-brand-border rounded-xl px-3 py-2 text-sm text-brand-ink" placeholder="New feature: Forager Guide!" />
            </label>
            <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">Body</span>
                <textarea data-testid="content-body" value={form.body} onChange={(e) => set('body', e.target.value)} rows={2} className="mt-1 w-full bg-brand-card border border-brand-border rounded-xl px-3 py-2 text-sm text-brand-ink" placeholder="Describe it…" />
            </label>

            <div className="grid grid-cols-2 gap-2">
                <label className="block">
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">Link URL (optional)</span>
                    <input data-testid="content-link-url" value={form.link_url} onChange={(e) => set('link_url', e.target.value)} className="mt-1 w-full bg-brand-card border border-brand-border rounded-xl px-3 py-2 text-sm text-brand-ink" placeholder="/marketplace or https://…" />
                </label>
                <label className="block">
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">Link label</span>
                    <input data-testid="content-link-label" value={form.link_label} onChange={(e) => set('link_label', e.target.value)} className="mt-1 w-full bg-brand-card border border-brand-border rounded-xl px-3 py-2 text-sm text-brand-ink" placeholder="Open" />
                </label>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
                <ImagePicker testId="content-image" onSelect={uploadImg}>
                    {(open2) => (
                        <button type="button" onClick={open2} className="inline-flex items-center gap-2 text-sm bg-brand-card border border-brand-border rounded-full px-3 py-2 cursor-pointer text-brand-ink2">
                            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Image
                        </button>
                    )}
                </ImagePicker>
                {form.image && (
                    <span className="inline-flex items-center gap-1 text-xs text-brand-green"><ImageIcon size={13} /> attached
                        <button onClick={() => set('image', '')} className="text-brand-ink3 hover:text-brand-terracotta ml-1"><X size={12} /></button>
                    </span>
                )}
                {form.type === 'banner' && (
                    <>
                        <label className="inline-flex items-center gap-1.5 text-xs text-brand-ink3">Bg <input type="color" data-testid="content-bg" value={form.bg_color} onChange={(e) => set('bg_color', e.target.value)} className="w-8 h-8 rounded-lg border border-brand-border" /></label>
                        <label className="inline-flex items-center gap-1.5 text-xs text-brand-ink3">Text <input type="color" data-testid="content-text-color" value={form.text_color} onChange={(e) => set('text_color', e.target.value)} className="w-8 h-8 rounded-lg border border-brand-border" /></label>
                    </>
                )}
                <label className="inline-flex items-center gap-2 text-sm text-brand-ink2 cursor-pointer">
                    <input type="checkbox" data-testid="content-featured" checked={!!form.featured} onChange={(e) => set('featured', e.target.checked)} className="w-4 h-4 accent-amber-400" />
                    <span className="inline-flex items-center gap-1"><Star size={13} className="text-amber-400" /> Featured (top)</span>
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-brand-ink2 cursor-pointer ml-auto">
                    <input type="checkbox" data-testid="content-active" checked={form.active} onChange={(e) => set('active', e.target.checked)} className="w-4 h-4 accent-brand-green" /> Active
                </label>
            </div>

            <div className="grid grid-cols-2 gap-2">
                <label className="block">
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">Show from (optional)</span>
                    <input type="date" data-testid="content-start" value={form.start_at} onChange={(e) => set('start_at', e.target.value)} className="mt-1 w-full bg-brand-card border border-brand-border rounded-xl px-3 py-2 text-sm text-brand-ink" />
                </label>
                <label className="block">
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">Hide after (optional)</span>
                    <input type="date" data-testid="content-end" value={form.end_at} onChange={(e) => set('end_at', e.target.value)} className="mt-1 w-full bg-brand-card border border-brand-border rounded-xl px-3 py-2 text-sm text-brand-ink" />
                </label>
            </div>

            <div className="flex gap-2 pt-1">
                <button onClick={save} disabled={saving} data-testid="content-save" className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-green hover:bg-brand-greenDark text-white py-2.5 rounded-full text-sm font-medium disabled:opacity-50">
                    {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save block
                </button>
                <button onClick={onCancel} className="px-4 py-2.5 rounded-full text-sm bg-brand-card border border-brand-border text-brand-ink2">Cancel</button>
            </div>
        </div>
    );
}

export default function AdminStudio() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [confirmId, setConfirmId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try { const r = await api.get('/admin/app-content'); setItems(r.data || []); }
        catch { toast.error('Could not load content'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        if (user && !user.is_admin) { navigate('/'); return; }
        if (user) load();
    }, [user, load, navigate]);

    if (!user || !user.is_admin) return null;

    const remove = async (it) => {
        if (confirmId !== it.id) { setConfirmId(it.id); setTimeout(() => setConfirmId((c) => (c === it.id ? null : c)), 3000); return; }
        setConfirmId(null);
        try { await api.delete(`/admin/app-content/${it.id}`); toast.success('Deleted'); load(); }
        catch { toast.error('Could not delete'); }
    };
    const toggle = async (it) => {
        try { await api.put(`/admin/app-content/${it.id}`, { ...it, active: !it.active }); load(); }
        catch { toast.error('Could not update'); }
    };
    const move = async (idx, dir) => {
        const j = idx + dir;
        if (j < 0 || j >= items.length) return;
        const a = items[idx], b = items[j];
        try { await api.put(`/admin/app-content/${a.id}`, { ...a, order: b.order }); await api.put(`/admin/app-content/${b.id}`, { ...b, order: a.order }); load(); }
        catch { toast.error('Could not reorder'); }
    };
    const onSaved = () => { setEditing(null); load(); };
    const nextOrder = items.length ? Math.max(...items.map((i) => i.order || 0)) + 1 : 0;

    return (
        <div className="px-5 pt-10 pb-32 max-w-2xl mx-auto" data-testid="admin-studio">
            <button data-testid="admin-studio-back" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/profile'))} className="inline-flex items-center gap-1 text-sm text-brand-ink3 hover:text-brand-ink mb-6"><ArrowLeft size={14} /> Back</button>
            <div className="flex items-center justify-between mb-2">
                <div>
                    <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">Admin</p>
                    <h1 className="font-serif text-3xl sm:text-4xl text-brand-ink mt-1 inline-flex items-center gap-2"><Wand2 size={26} /> App Studio</h1>
                </div>
                {!editing && (
                    <button data-testid="add-content-btn" onClick={() => setEditing({ ...EMPTY, order: nextOrder })} className="inline-flex items-center gap-1.5 text-sm bg-brand-green hover:bg-brand-greenDark text-white rounded-full px-3.5 py-2 font-medium">
                        <Plus size={15} /> Add
                    </button>
                )}
            </div>
            <p className="text-sm text-brand-ink3 mb-5">Add banners &amp; cards to your app yourself — no code. They appear instantly on the chosen screen.</p>

            {editing && (
                <div className="mb-6">
                    <ContentForm initial={editing.id ? editing : { ...EMPTY, order: nextOrder }} onCancel={() => setEditing(null)} onSaved={onSaved} />
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-20 text-brand-ink3"><Loader2 className="animate-spin" /></div>
            ) : items.length === 0 ? (
                <p className="text-sm text-brand-ink3 py-10 text-center">No content blocks yet. Tap “Add” to create one.</p>
            ) : (
                <div className="space-y-2" data-testid="content-list">
                    {items.map((it, i) => (
                        <div key={it.id} data-testid={`content-row-${it.id}`} className={`flex items-center gap-3 bg-brand-card border border-brand-border rounded-2xl p-3 ${it.active ? '' : 'opacity-55'}`}>
                            <span className="text-[9px] uppercase font-bold px-2 py-0.5 rounded-full bg-brand-bg2 text-brand-ink3 flex-shrink-0">{it.type}</span>
                            {it.featured && <Star size={13} className="text-amber-400 flex-shrink-0" fill="currentColor" />}
                            {scheduleState(it) && <span className={`text-[9px] uppercase font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${scheduleState(it).cls}`}>{scheduleState(it).label}</span>}
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-brand-ink truncate">{it.title || it.body || '(untitled)'}</div>
                                <div className="text-[11px] text-brand-ink3">{it.placement}</div>
                            </div>
                            <div className="flex items-center gap-0.5 flex-shrink-0 text-brand-ink3">
                                <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`content-up-${it.id}`} className="p-1.5 hover:text-brand-ink disabled:opacity-30"><ChevronUp size={16} /></button>
                                <button onClick={() => move(i, 1)} disabled={i === items.length - 1} data-testid={`content-down-${it.id}`} className="p-1.5 hover:text-brand-ink disabled:opacity-30"><ChevronDown size={16} /></button>
                                <button onClick={() => toggle(it)} data-testid={`content-toggle-${it.id}`} className="p-1.5 hover:text-brand-ink">{it.active ? <Eye size={16} /> : <EyeOff size={16} />}</button>
                                <button onClick={() => setEditing(it)} data-testid={`content-edit-${it.id}`} className="p-1.5 hover:text-brand-green"><Pencil size={15} /></button>
                                <button onClick={() => remove(it)} data-testid={`content-delete-${it.id}`} className={`p-1.5 ${confirmId === it.id ? 'text-brand-terracotta animate-pulse' : 'hover:text-brand-terracotta'}`} title={confirmId === it.id ? 'Tap again to delete' : 'Delete'}><Trash2 size={15} /></button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
